'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Share2,
  Printer,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Calendar,
  BedSingle,
  Warehouse,
  Home,
  Expand,
  X,
  Phone,
  Mail,
} from 'lucide-react'
import { cn } from '@/utilities/ui'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { Property } from '@/payload-types'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import ShareButton from '../ShareButton'
import { getUrl } from '@/utilities/getShareUrl'
import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { formatPriceDZD, formatPricePerSqm } from '@/utilities/PriceFormat'
import { useTheme } from '@/providers/Theme'
import PropertyImageGallery from './PropertyImageGallery'
import MuxPlayer from '@mux/mux-player-react'
import { ImageMedia } from '../Media/ImageMedia'
dayjs.extend(relativeTime)

const PropertyDetailPage = ({
  className,
  property,
  lang,
  dictionary,
}: {
  className: string
  property: Property
  lang: string
  dictionary: any
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const { theme } = useTheme()
  const prevRef = useRef<any>(null)
  const nextRef = useRef<any>(null)
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: (index: any) => setCurrentPhotoIndex(index),
    nextArrow: <SamplePrevArrow />,
    prevArrow: <SampleNextArrow />,
  }

  const [isOpen, setIsOpen] = useState(false)

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const params = useParams()

  const shareUrl = useMemo(() => {
    return getUrl({
      slug: `properties/${property?.slug}`,
      pathname,
      searchParams,
      params,
    })
  }, [pathname, searchParams, params])

  function getImagePreview(property: any) {
    return (
      property?.mediaSection?.images?.[0]?.image?.url || '/images/no-product-image-available.webp'
    )
  }

  // Translate listing status
  const listedInTranslation =
    property?.propertyDescription?.listedIn === 'for_sale'
      ? dictionary?.propertyDetail?.forSale || 'For Sale'
      : dictionary?.propertyDetail?.forRent || 'For Rent'

  // Translate category
  const categoryTranslations: Record<string, string> = {
    apartment: dictionary?.propertyDetail?.categoryValues?.apartment || 'Apartment',
    villa: dictionary?.propertyDetail?.categoryValues?.villa || 'Villa',
    house: dictionary?.propertyDetail?.categoryValues?.house || 'House',
    office: dictionary?.propertyDetail?.categoryValues?.office || 'Office',
    land: dictionary?.propertyDetail?.categoryValues?.land || 'Land',
  };
    
  const categoryValue = property?.propertyDescription?.category;
  const categoryTranslation = categoryValue ? categoryTranslations[categoryValue] : '';

  useEffect(() => {
    // Increment view count
    fetch(`/api/properties/${property.id}/increment-view`, {
      method: 'POST',
    }).catch(console.error)
  }, [property.id])

  const mediaItems = [
    ...(property?.mediaSection?.images?.map((img) => ({
      type: 'image',
      data: img.image,
    })) || []),
    ...(property?.mediaSection?.videos?.map((vid) => ({
      type: 'video',
      data: vid.video,
    })) || []),
  ]

  return (
    <div className={cn(' bg-light md:bg-card w-full pt-[60px]', className)}>
      {/* Property Header */}
      <div className="mx-auto container w-full hidden md:grid grid-cols-2 mb-5">
        <div className="flex flex-col justify-between gap-1">
          <div className="flex  flex-col">
            <h1 className="text-typography text-h2 font-semibold">{property?.title}</h1>
            <p className="text-sm text-muted">{property?.locationSection?.address}</p>
          </div>

          {/* Status and Listing Info */}
          <div className="flex items-center  gap-4">
            <span className="text-h5 flex items-center text-green-600">
              <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
              {listedInTranslation}
            </span>
          </div>
        </div>
        <div className="h-full flex flex-col justify-between items-end gap-5">
          {/* Share and Print Buttons */}

          <ShareButton
            url={shareUrl}
            media={getImagePreview(property)}
            title={property?.title}
            description={property?.propertyDescription?.description}
            shareLabel={dictionary?.propertyDetail?.shareLabel}
          />

          <div className="text-typography text-h5 font-semibold">
            {formatPriceDZD(property?.propertyDescription?.price, lang)}
          </div>
          <div className="text-h5 text-muted">
            {property?.propertyDescription?.price &&
              property?.detailSection?.surface &&
              formatPricePerSqm(property?.propertyDescription?.price, property?.detailSection?.surface)
            }
          </div>
        </div>
      </div>

      {/* Property Photos - Mobile (Carousel) / Desktop (Grid) */}
      <div className="block mx-auto md:hidden !relative mt-4 p-3">
        {/* Mobile Carousel */}

        {mediaItems?.length > 0 && (
          <Slider {...sliderSettings}>
            {mediaItems.map((media, index) => (
              <div key={index} className="relative">
                {media.type === 'image' ? (
                  <ImageMedia
                    resource={media.data}
                    imgClassName="w-full sm:h-[421px] h-[300px] object-cover rounded-2xl"
                    size="(max-width: 768px) 100vw, 500px"
                    alt={`${property?.title} - ${dictionary?.propertyDetail?.photo || 'Photo'} ${index + 1}`}
                    priority={index === 0}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                ) : (
                  <MuxPlayer
                    playbackId={media.data?.mux_playback_id}
                    accentColor="#006633"
                    className="w-full sm:h-[421px] h-[300px] object-cover rounded-2xl"
                    autoPlay={false}
                  />
                )}
              </div>
            ))}
          </Slider>
        )}
      </div>

      {/* Desktop Grid Layout */}
      {mediaItems.length > 0 && (
        <div
          className={`hidden mx-auto container md:grid gap-2 h-[600px] mb-6 ${
            mediaItems.length > 2
              ? 'grid-cols-4 grid-rows-2'
              : mediaItems.length === 2
                ? 'grid-cols-2 grid-rows-1'
                : 'grid-cols-1 grid-rows-1'
          }`}
        >
          {mediaItems.slice(0, 3).map((media, index) => {
            return (
              <div
                key={index}
                className={`relative ${
                  mediaItems.length === 1
                    ? 'col-span-1 row-span-1'
                    : mediaItems.length === 2
                    ? 'col-span-1 row-span-1'
                    : index === 0
                    ? 'col-span-3 row-span-2'
                    : ''
                }`}
              >
                {media.type === 'image' ? (
                  <ImageMedia
                    resource={media.data}
                    imgClassName={`w-full h-full object-cover ${
                      index === 0 && mediaItems.length === 1
                        ? 'rounded-2xl'
                        : index === 0
                          ? 'rounded-l-2xl'
                          : 'rounded-r-2xl'
                    }`}
                    fill
                    alt={`${property?.title} - ${dictionary?.propertyDetail?.photo || 'Photo'} ${index + 1}`}
                    priority={index === 0}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                ) : (
                  <MuxPlayer
                    playbackId={media.data?.mux_playback_id}
                    accentColor="#006633"
                    className={`w-full h-full object-cover ${
                      index === 0 && mediaItems.length === 1
                        ? 'rounded-2xl'
                        : index === 0
                          ? 'rounded-l-2xl'
                          : 'rounded-r-2xl'
                    }`}
                  />
                )}

                {/* Show "See All" only if more than one media */}
                {mediaItems.length > 1 && index === 2 && (
                  <div className="absolute inset-0 flex items-end justify-end bg-black bg-opacity-50 rounded-r-2xl">
                    <button
                      onClick={() => setIsOpen(true)}
                      className="bg-white text-black px-4 py-2 rounded mb-6 ltr:mr-6 rtl:ml-6"
                    >
                      {dictionary?.propertyDetail?.seeAllPhotos || 'See All'}{' '}
                      {mediaItems.length}{' '}
                      {dictionary?.propertyDetail?.photos || 'Photos'}
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Desktop Property Features */}
      <div className="hidden md:grid grid-cols-2 mx-auto container md:grid-cols-3 lg:grid-cols-4 gap-4 pb-5">
        
        {/* BEDROOM PIECES */}
        {property?.propertyDescription?.category !== 'land' && (
          <div className="flex items-center gap-4">
            <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
              <BedSingle size={20} className="!stroke-[1px]" />
            </div>

            <div className="text-typography flex flex-col justify-between gap-1">
              <div className="text-h5 font-semibold">
                {dictionary?.propertyDetail?.pieces || 'Pieces'}
              </div>
              <div className="text-h5 text-muted">{property?.detailSection?.rooms}</div>
            </div>
          </div>
        )}

        {/* BATHROOMS */}
        {property?.propertyDescription?.category !== 'land' && (
          <div className=" flex items-center gap-4">
            <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
              <img src="/icons/shower.svg" alt="shower" className="w-[20px] h-[20px]" />
            </div>

            <div className="text-typography flex flex-col justify-between gap-1">
              <div className="text-h5 font-semibold">{dictionary?.propertyDetail?.bathrooms}</div>
              <div className="text-h5 text-muted capitalize">{property?.detailSection.bathrooms}</div>
            </div>
          </div>
        )}

        {/* SURFACE */}
        <div className=" flex items-center gap-4">
          <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
            <img src="/icons/expand.svg" alt="expand" className="w-[20px] h-[20px]" />
          </div>

          <div className="text-typography flex flex-col justify-between gap-1">
            <div className="text-h5 font-semibold">
              {dictionary?.propertyDetail?.surface || 'Surface'}
            </div>
            <div className="text-h5 text-muted">{property?.detailSection?.surface} m²</div>
          </div>
        </div>

        {/* PROPERTY TYPE */}
        <div className=" flex items-center gap-4">
          <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
            <Home size={20} className="!stroke-[1px]" />
          </div>

          <div className="text-typography flex flex-col justify-between gap-1">
            <div className="text-h5 font-semibold">
              {dictionary?.propertyDetail?.propertyType || 'Property Type'}
            </div>
            <div className="text-h5 text-muted capitalize">
              {categoryTranslation}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile view - Property info section */}
      <div className="block md:hidden mx-auto container px-0 max-md:px-4 mt-5">
        <div className="w-full sm:grid sm:grid-cols-2 mb-4 items-center">
          <div className="flex flex-col gap-7">
            <div className="flex  flex-col gap-2">
              {/* Price */}
              <div className="sm:hidden flex items-baseline gap-4">
                <p className="text-typography text-mobile-h2 font-semibold">
                  {formatPriceDZD(property.propertyDescription?.price, lang)}
                </p>
                <p className=" text-muted">{property?.detailSection?.surface} / m²</p>
              </div>
              <h1 className="text-typography sm:text-h2 text-mobile-h2 font-semibold">
                {property?.propertyDescription?.title}
              </h1>
              <p className="text-typography">{property?.locationSection?.address}</p>
              <div className="sm:hidden flex items-center  gap-4">
                <span className="flex items-center text-green-600">
                  <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                  {listedInTranslation}
                </span>

                <div className="text-typography flex items-center justify-center gap-2">
                  {theme === 'dark' ? (
                    <img src="/icons/expand-w.svg" alt="expand" />
                  ) : (
                    <img src="/icons/expand.svg" alt="expand" />
                  )}

                  <p className="text-muted">
                    {property?.detailSection?.surface} m²
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="h-full flex flex-col justify-between items-end">
            {/* Share and Print Buttons */}

            {/* Price */}
            <div className="sm:mt-[10px] hidden sm:flex items-baseline gap-4">
              <p className="text-typography text-mobile-h2 font-semibold">
                {formatPriceDZD(property?.propertyDescription?.price, lang)}
              </p>
              <p className="text-sm text-muted">{property?.detailSection?.surface} / m²</p>
            </div>

            {/* Status and Listing Info */}
            <div className="hidden sm:flex items-center  gap-4">
              <p className="flex items-center text-green-600">
                <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                {listedInTranslation}
              </p>

              <div className="text-typography flex items-center justify-center gap-2">
                {theme === 'dark' ? (
                  <img src="/icons/expand-w.svg" alt="expand" />
                ) : (
                  <img src="/icons/expand.svg" alt="expand" />
                )}
                <p className="text-muted">
                  {property?.detailSection?.surface} m²
                </p>
              </div>
            </div>
          </div>
        </div>

        <a
            href={`tel:${property?.agency?.phone}`}
            className="text-typography hover:text-black flex items-center justify-center gap-2 border border-gray-500 rounded-lg py-3 px-4 hover:bg-gray-50 transition-colors"
          >
            <Phone size={20} />
            <span className="text-[15px] font-semibold">
              {dictionary?.propertyDetail?.contactAgent || 'Contact Agent'}
            </span>
        </a>

        <div className={cn('hidden sm:block bg-card shadow-sm rounded-xl p-[30px]')}>
          <h2 className="text-typography text-[17px] font-semibold mb-5">
            {dictionary?.propertyDetail?.overview || 'Overview'}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
                <BedSingle size={20} className="!stroke-[1px]" />
              </div>

              <div className=" flex flex-col justify-between">
                <div className="text-typography text-h5 font-semibold">
                  {dictionary?.propertyDetail?.pieces || 'Pieces'}
                </div>
                <div className="text-h5 text-muted">
                  {formatPriceDZD(property?.propertyDescription?.price, lang)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.7391 1.11133H9.40577C8.30654 1.11427 7.25325 1.55262 6.47649 2.33042C5.69973 3.10821 5.26279 4.16209 5.26133 5.26133H4.07244C3.28755 5.2628 2.53524 5.57524 1.98024 6.13024C1.42524 6.68524 1.1128 7.43756 1.11133 8.22244V10.0002C1.11133 10.1579 1.17396 10.3091 1.28544 10.4206C1.39692 10.532 1.54812 10.5947 1.70577 10.5947H10.0002C10.1579 10.5947 10.3091 10.532 10.4206 10.4206C10.532 10.3091 10.5947 10.1579 10.5947 10.0002V8.22244C10.5932 7.43756 10.2807 6.68524 9.72575 6.13024C9.17075 5.57524 8.41844 5.2628 7.63355 5.26133H6.44466C6.44613 4.47644 6.75857 3.72413 7.31357 3.16913C7.86857 2.61413 8.62089 2.30169 9.40577 2.30022H14.7391C15.524 2.30169 16.2763 2.61413 16.8313 3.16913C17.3863 3.72413 17.6987 4.47644 17.7002 5.26133V18.2947H18.8891V5.26133C18.8876 4.16113 18.4499 3.10642 17.672 2.32846C16.894 1.5505 15.8393 1.1128 14.7391 1.11133ZM7.628 6.44466C8.09949 6.44466 8.55168 6.63196 8.88507 6.96536C9.21847 7.29876 9.40577 7.75094 9.40577 8.22244V9.40577H2.29466V8.22244C2.29466 7.75094 2.48196 7.29876 2.81536 6.96536C3.14876 6.63196 3.60094 6.44466 4.07244 6.44466H7.628Z"
                    fill="black"
                  />
                  <path d="M1.70508 12.373H2.88841V16.523H1.70508V12.373Z" fill="black" />
                  <path d="M4.07227 12.373H5.2556V14.1508H4.07227V12.373Z" fill="black" />
                  <path d="M4.07227 15.9277H5.2556V18.3H4.07227V15.9277Z" fill="black" />
                  <path d="M6.44531 12.373H7.62865V15.3342H6.44531V12.373Z" fill="black" />
                  <path d="M6.44531 16.5176H7.62865V17.7009H6.44531V16.5176Z" fill="black" />
                  <path d="M8.81641 12.373H9.99974V14.1508H8.81641V12.373Z" fill="black" />
                  <path d="M8.81641 15.334H9.99974V18.8895H8.81641V15.334Z" fill="black" />
                </svg>
              </div>

              <div className="flex flex-col justify-between gap-1">
                <div className="text-typography text-h5 font-semibold">
                  {dictionary?.propertyDetail?.rooms || 'Units'}
                </div>
                <div className="text-h5 text-muted">{property?.detailSection?.rooms}</div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
                <Calendar size={20} className="!stroke-[1px]" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
                <Warehouse size={20} className="!stroke-[1px]" />
              </div>

              <div className="flex flex-col justify-between gap-1">
                <div className="text-typography text-h5 font-semibold">
                  {dictionary?.propertyListing?.features?.garage || 'Garage'}
                </div>
                <div className="text-h5 text-muted">{property?.detailSection?.garages}</div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
                <img src="/icons/expand.svg" alt="" className="w-[20px] h-[20px]" />
              </div>

              <div className="flex flex-col justify-between gap-1">
                <div className="text-typography text-h5 font-semibold">
                  {dictionary?.propertyDetail?.surface || 'Surface'}
                </div>
                <div className="text-h5 text-muted">{property?.detailSection?.surface} m²</div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="bg-white flex items-center justify-center w-[50px] h-[50px] rounded-lg !p-0 border border-border">
                <Home size={20} className="!stroke-[1px]" />
              </div>

              <div className="flex flex-col justify-between gap-1">
                <div className="text-typography text-h5 font-semibold">
                  {dictionary?.propertyDetail?.propertyType || 'Property Type'}
                </div>
                <div className="text-h5 text-muted capitalize">
                  {categoryTranslation}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PropertyImageGallery
        property={property}
        dictionary={dictionary}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        lang={lang}
      />
    </div>
  )
}

export default PropertyDetailPage

function SampleNextArrow(props) {
  const { onClick } = props
  return (
    <button
      onClick={onClick}
      className="absolute left-[20px] top-1/2 -translate-y-1/2 z-10 bg-white text-black p-2 rounded-full shadow-md hover:bg-opacity-80"
    >
      <ChevronLeft className="w-6 h-6" />
    </button>
  )
}

function SamplePrevArrow(props) {
  const { onClick } = props
  return (
    <button
      onClick={onClick}
      className="absolute right-[20px] top-1/2 -translate-y-1/2 z-10 bg-white text-black p-2 rounded-full shadow-md hover:bg-opacity-80"
    >
      <ChevronRight className="w-6 h-6" />
    </button>
  )
}
